<template lang="pug">
.product-access
  CardMinimal(:title="$t('products.ticket_unlocks')")
    .p-a-4
      FormRadio(:items="ticketAccess", @input="ticketAccess = $event.value")
    .p-a-4.border-light-top
      p {{ $t('products.selection').toUpperCase() }}
      FormDropdown(:items="contentSelection", @input="contentSelection = $event.value").f-w.m-t-2
      small(v-if="contentSelection[0].selected") {{ $t('products.add_manually') }}
  CardMinimal(:title="$t('products.validity_period')").m-t-4
    .p-a-4
      .d-f.f-g.fd-c.fd-md-r
        .f-1
          FormDropdown(:items="validitySelection", @input="validitySelection = $event.value")
        .f-1
          FormInput(type="date", v-if="data.expiration === 'expires_at'", v-model="data.access.expires_at", :min="new Date().toISOString().split('T')[0]", @input="setExpirationValue($event)", :class="`${dateError === 'expires_at' ? 'error' : ''}`", id="access-date")
          FormInput(type="text", v-else-if="data.expiration === 'expires_after'", placeholder="48h", v-model="data.access.expires_after", @input="setExpirationValue($event)", :class="`${dateError === 'expires_after' ? 'error' : ''}`", :tooltip="true", :tooltipText="expireAfterTooltip", id="access-format")
</template>

<script>

export default {
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    editData: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    expireAfterTooltip() {
      return this.$t('tooltips.expiration').split('\n').reduce((tip, line) => `${tip}<p>${line}</p>`);
    },
    /**
     * Get and setters for ticketAccess
     */
    ticketAccess: {
      /**
       * Getter
       * @returns {Array}
       */
      get() {
        const { selectedTicketAccess } = this;
        return [
          { value: true, string: this.$t('products.single_within_the_selection'), description: this.$t('products.single_within_the_selection_description'), icon: 'play-box-outline' },
          { value: false, string: this.$t('products.all_within_the_selection'), description: this.$t('products.all_within_the_selection_description'), icon: 'animation-play-outline' },
        ].map(item => ({ ...item, selected: item.value === selectedTicketAccess }));
      },
      /**
       * Setter
       * @param {String} value
       */
      set(value) {
        this.selectedTicketAccess = value;
        this.data.access.single_access = value;
        this.$emit('data', this.data.access);
      },
    },
    /**
     * Get and setters for contentSelection
     */
    contentSelection: {
      /**
       * Getter
       * @returns {Array}
       */
      get() {
        const { selectedContentSelection } = this;
        return [
          { value: [], string: this.$t('products.dont_add_automatically') },
          { value: ['live:*'], string: this.$t('products.available_all_broadcasts') },
          { value: ['video:*'], string: this.$t('products.available_all_videos') },
          { value: ['live:*', 'video:*'], string: this.$t('products.available_all_broadcasts_and_videos') },
        ].map(item => ({ ...item, selected: JSON.stringify(item.value) === JSON.stringify(selectedContentSelection) }));
      },
      /**
       * Setter
       * @param {Array} value
       */
      set(value) {
        this.selectedContentSelection = value;
        this.data.access.content = this.data.access.content.filter(c => c !== 'live:*' && c !== 'video:*').concat(value)
        this.$emit('data', this.data.access);
      },
    },
    /**
     * Get and setters for validitySelection
     */
    validitySelection: {
      /**
       * Getter
       * @returns {Array}
       */
      get() {
        const { data: { expiration } } = this;
        return [
          { value: 'expires_at', string: this.$t('products.to_a_specific_date') },
          { value: 'expires_after', string: this.$t('products.valid_for') }
        ].map(item => ({ ...item, selected: item.value === expiration }));
      },
      /**
       * Setter
       * @param {String} value
       */
      set(value) {
        this.data.expiration = value;

        if (value === 'expires_at') {
          delete this.data.access.expires_after;
          if (this.edit) {
            this.data.access.expires_at = this.editData.access.expires_at ? new Date(this.editData.access.expires_at).toISOString().split('T')[0] : '';
          }
        }

        if (value === 'expires_after') {
          delete this.data.access.expires_at;
          this.data.access.expires_after = this.edit ? this.editData.access.expires_after || '48h' : '48h';
        }

        this.$emit('data', this.data.access);
      },
    },
  },
  methods: {
    /**
     * Set value for expiration
     */
    setExpirationValue(value) {
      const { expiration } = this.data;
      this.data.access[expiration] = expiration === 'expires_at' ? new Date(value).toISOString().split('T')[0] : value;
      this.$emit('data', this.data.access);
    },
    /**
     * Will be run by the parent when updating data
     */
    async update() {
      const { access: { single_access, content }, expiration: expirationType } = this.data;
      const expiration = this.data.access[expirationType];

      if (single_access !== this.editData.access.single_access || expiration !== this.editData.access[expirationType]) {
        const payload = {
          access: { single_access },
        };
        payload.access[expirationType] = expiration;

        await this.$store.dispatch('Products/updateProduct', {
          id: this.editData.id,
          payload,
        });
      }

      if (JSON.stringify(content) !== JSON.stringify(this.editData.access.content)) {
        await this.$store.dispatch('Products/updateContent', {
          id: this.editData.id,
          payload: content,
        });
      }
    },
    /**
     * Validate the access
     */
    async validate() {
      this.dateError = null;
      if (this.data.expiration === 'expires_at') {
        if (this.data.access.expires_at === '') {
          this.dateError = 'expires_at';
          return false;
        }
        if (new Date(this.data.access.expires_at) === "Invalid Date" && isNaN(new Date(this.data.access.expires_at))) {
          this.dateError = 'expires_at';
          return false;
        }
      }

      if (this.data.expiration === 'expires_after') {
        if (this.data.access.expires_after === '') {
          this.dateError = 'expires_after';
          return false;
        }
      }

      return true;
    },
  },
  data() {
    return {
      dateError: null,
      data: {
        expiration: 'expires_after',
        access: {
          single_access: false,
          content: [],
          expires_after: '48h',
        },
      },
      selectedTicketAccess: true,
      selectedContentSelection: [],
    }
  },
  mounted() {
    const { editData, edit } = this;
    if (editData.access && edit) {
      this.data.access.content = editData.access.content;
      this.ticketAccess = editData.access.single_access;
      this.contentSelection = editData.access.content.filter(c => c === 'live:*' || c === 'video:*');
      this.validitySelection = typeof editData.access.expires_after !== 'undefined' ? 'expires_after' : 'expires_at';
    } else {
      // this.setExpirationValue(new Date(new Date().setDate(new Date().getDate() + 7)));
    }

    this.$emit('data', this.data.access);
  },
  beforeDestroy() {
    this.$emit('data', null);
  }
};
</script>